/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { MasterData, PRIME_VENDOR_ROLE, USER_PROCESSES, UserType } from '../../../Shared/Constants/App';
import { Loader } from '../../Jobs/Components/JobView/JobsView.styles';
import PageTitle from '../../../Shared/Components/Common/PageTitle/PageTitle';
import { ReportsContainer } from './Reports.styles';
import { getStoredCustomerDetails, hasEventForReport } from '../../../Shared/Utilities/utils';
import Button, { ButtonType } from '../../../Shared/Components/Common/Buttons/Button';
import ReportApiService from '../Services/ReportService';
import CustomerApiService from '../../Customer/Services/CustomerService';
import MasterDataApiService from '../../../Shared/Services/MasterDataService';

interface ReportConfig {
  role: string;
  dashboardNumber: number;
  reportType: string;
  label: string;
}

const Reports = () => {
  const { t } = useTranslation(['dashboard', 'reports']);
  const [reportUrl, setReportUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [showReportMenu, setShowReportMenu] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<UserType>(UserType.NoUser);
  const [reportConfigs, setReportConfigs] = useState<ReportConfig[]>([]);
  const [processEvents, setProcessEvents] = useState([]);
  const [isProcessEventsLoading, setIsProcessEventsLoading] = useState(false);

  const { customerId = '', userId } = getStoredCustomerDetails() || {};

  const getUserRole = (): UserType => {
    const authValue = localStorage.getItem('auth');
    return authValue ? JSON.parse(authValue)?.attributes?.['custom:Role'] : UserType.NoUser;
  };

  const getReportConfigsFromMasterData = async (role: UserType) => {
    const [{ data }, multiCustomerOrgIds] = await Promise.all([
      ReportApiService.getMasterData(MasterData.DASHBOARD_NUMBER, 20),
      MasterDataApiService.getMultiCustomerOrgIds(),
    ]);

    const applicableConfigs = data
      .filter((item: any) => item.value.includes(role))
      .map(
        (item: any): ReportConfig => ({
          role: item.value,
          dashboardNumber: item.keyCode.split('DASHBOARD_NUMBER_')[1],
          reportType: item.subKey1Value,
          label: item.subKey2Value,
        })
      );

    // Vendors of a multi-customer organization (Velociti, UNIFI) get the prime vendor dashboards;
    // everyone else gets the dashboards mapped to their own role. The process/events check below
    // decides which of those dashboards the user can actually open.
    const isMultiCustomerOrgUser = multiCustomerOrgIds.includes(customerId as string);
    const filteredConfigs: ReportConfig[] = applicableConfigs.filter((item: ReportConfig) =>
      isMultiCustomerOrgUser ? item.role === PRIME_VENDOR_ROLE : item.role !== PRIME_VENDOR_ROLE
    );

    setReportConfigs(filteredConfigs);
    return filteredConfigs;
  };

  const loadReportUrl = async () => {
    setLoading(true);
    try {
      const role = getUserRole();
      setUserRole(role);

      const configs = await getReportConfigsFromMasterData(role);

      if (configs.length > 1) {
        setShowReportMenu(true);
        return;
      }

      if (configs.length === 1) {
        const url = await ReportApiService.fetchReportUrl({
          reportType: configs[0].reportType,
          dashboardNumber: configs[0].dashboardNumber,
          orgId: customerId,
          role,
          userId,
        });
        setReportUrl(url.data);
      } else {
        console.warn('No report configuration found for role:', role);
      }
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReportFetch = async (config: ReportConfig) => {
    setLoading(true);
    try {
      const url = await ReportApiService.fetchReportUrl({
        reportType: config.reportType,
        dashboardNumber: config.dashboardNumber,
        orgId: customerId,
        role: userRole,
        userId,
      });
      setReportUrl(url.data);
      setShowReportMenu(false);
    } catch (error) {
      console.error('Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProcessEvents = async () => {
    try {
      setIsProcessEventsLoading(true);
      if (userId && customerId) {
        const processEvents = await CustomerApiService.fetchProcessEvents(
          userId,
          customerId,
          Number(USER_PROCESSES.Reports)
        );
        setProcessEvents(processEvents?.data);
      }
      setIsProcessEventsLoading(false);
    } catch (error) {
      console.error('Error fetching process events:', error);
    } finally {
      setIsProcessEventsLoading(false);
    }
  };

  useEffect(() => {
    loadReportUrl();
    // Fetch process/events for all user types (velociti vendor, customer and sub vendor users)
    // so report buttons are shown based on the actual process/event access mapped in the backend,
    // instead of defaulting to showing all reports for customer/non-velociti users.
    fetchProcessEvents();
  }, []);

  return (
    <>
      <PageTitle
        title={t('dashboard:Reports')}
        onCancelClick={() => setShowReportMenu(true)}
        showCancelIcon={!showReportMenu}
      />
      <ReportsContainer>
        {(loading || isProcessEventsLoading) && (
          <Loader>
            <CircularProgress style={{ marginTop: '2rem', color: '#006B6B' }} />
          </Loader>
        )}

        {!loading && showReportMenu && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
            {reportConfigs
              .filter((config) => hasEventForReport(processEvents, config.reportType))
              .map((config) => (
                <Button
                  key={config.reportType}
                  label={config.label}
                  onClick={() => handleReportFetch(config)}
                  type={ButtonType.Primary}
                />
              ))}
          </div>
        )}

        {!loading && !showReportMenu && reportUrl && (
          <iframe
            id="report-iframe"
            src={reportUrl}
            frameBorder="0"
            width="100%"
            height="100%"
            title="Metabase Report"
            allowTransparency
          />
        )}

        {!loading && !showReportMenu && !reportUrl && <div>No reports available</div>}
      </ReportsContainer>
    </>
  );
};

export default Reports;
